import { InfinityIcon } from "lucide-react";
import type { PackForSaleRow } from "@/actions/packs";
import type { SimpleMediaData } from "@/db/schema/hiive";
import { formatPriceCompact } from "@/lib/currency";
import { perBookingMinor, savingAgainst } from "../lib/pricing";
import {
  PackPassChip,
  PackPassHeader,
  PackPassShell,
  PackPassStub,
  PackPassTitle,
} from "./pack-pass-shell";
import { PackSaleDescription } from "./pack-sale-description.client";
import type { PackStripContext } from "./pack-strip";

/**
 * A pack on sale, drawn as the ticket it becomes.
 *
 * The buy list used to be a settings row — icon, name, price, chevron — while
 * the same pack in the wallet was a ticket. Two drawings of one object read as
 * two features, and the row was the weaker of the two by a distance: it made
 * the most persuasive thing on the page (the saving) into caption text under a
 * name.
 */
export function PackSaleCard({
  pack,
  hiiveSlug,
  hiiveName,
  hiiveLogoImage,
  context,
}: {
  pack: PackForSaleRow;
  hiiveSlug: string;
  hiiveName: string;
  hiiveLogoImage: SimpleMediaData | null;
  /** A price in front of the buyer, when the surface knows one. */
  context?: PackStripContext;
}) {
  const perBooking = perBookingMinor(pack);
  const named = savingAgainst(perBooking, context?.listPriceMinor);
  const spread = named ? null : savingSpread(pack, perBooking);

  const bookings =
    pack.kind === "credit" && pack.creditsPerBooking && pack.creditCount
      ? Math.floor(pack.creditCount / Math.max(1, pack.creditsPerBooking))
      : null;

  return (
    <PackPassShell
      hiiveSlug={hiiveSlug}
      href={`/hiives/${hiiveSlug}/packs/${pack.slug}`}
    >
      <PackPassHeader
        hiiveName={hiiveName}
        logoImage={hiiveLogoImage}
        chip={
          named ? (
            <PackPassChip tone="brand">Save {named.percent}%</PackPassChip>
          ) : spread ? (
            <PackPassChip tone="brand">
              {spread.low === spread.high
                ? `Save ${spread.low}%`
                : `Save ${spread.low}–${spread.high}%`}
            </PackPassChip>
          ) : undefined
        }
      />

      <PackPassTitle>{pack.name}</PackPassTitle>

      {/* The hero is what you get, not what it costs — the price is the stub's
          job, the same way the wallet's stub carries the expiry. */}
      <div className="flex items-end gap-2.5 px-5 pt-3 pb-1">
        <span className="text-[2.75rem] leading-[0.85] font-semibold text-text-primary tabular-nums">
          {pack.kind === "window" ? (
            <InfinityIcon className="size-10" aria-hidden="true" />
          ) : (
            (bookings ?? pack.creditCount)
          )}
        </span>
        <span className="pb-1 text-[13px] text-text-secondary">
          {pack.kind === "window"
            ? "Unlimited access"
            : bookings != null
              ? bookings === 1
                ? "booking"
                : "bookings"
              : "credits"}
          {perBooking != null
            ? ` · ${formatPriceCompact(perBooking, pack.currency)} each`
            : ""}
        </span>
      </div>

      {/* The before-and-after, only where a real price was handed in. Allowed
          to wrap: truncating the most persuasive line mid-number is worse than
          a second line. */}
      {named && context ? (
        <p className="px-5 pb-1 text-[12.5px] leading-snug text-success dark:text-[#F5E6B8]">
          <span className="text-text-tertiary line-through">
            {formatPriceCompact(named.listPriceMinor, pack.currency)}
          </span>{" "}
          → {formatPriceCompact(named.packPriceMinor, pack.currency)} on{" "}
          {context.label}
        </p>
      ) : pack.description ? (
        <PackSaleDescription
          packName={pack.name}
          description={pack.description}
        />
      ) : null}

      <PackPassStub
        plainLabel
        label={formatPriceCompact(pack.priceAmountMinor, pack.currency)}
        value={
          pack.validityDays == null
            ? "No expiry"
            : pack.clockStart === "first_use"
              ? `${pack.validityDays} days from first use`
              : `${pack.validityDays} days`
        }
      />
    </PackPassShell>
  );
}

/**
 * The smallest and largest saving across everything a pack covers.
 *
 * A pack covering a ₹86,682 pass and a ₹1,84,753 one saves 8% on the first and
 * 57% on the second, and there is no single figure that describes both. Quoting
 * only the top end is the flattering lie; quoting only the bottom sells the
 * pack short. So both ends, and nothing when nothing is priced.
 */
export function savingSpread(
  pack: PackForSaleRow,
  perBooking: number | null,
): { low: number; high: number } | null {
  const percents: number[] = [];
  for (const target of pack.coveredTargets) {
    for (const price of [
      target.listPriceMinor,
      target.priceRange?.minMinor,
      target.priceRange?.maxMinor,
    ]) {
      const saving = savingAgainst(perBooking, price);
      if (saving) percents.push(saving.percent);
    }
  }
  if (percents.length === 0) return null;
  return { low: Math.min(...percents), high: Math.max(...percents) };
}
